import { Injectable } from '@angular/core';
import { LazyLoadEvent } from 'primeng/api';
import { ColumnFormat } from '../enums/column-format';
import * as objectHash from 'object-hash';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  constructor() {
  }

  /**
   * make copy of JSON-Object (by value)
   * @param objectToCopy
   */
  jsonCopy(objectToCopy: any): any {
    const jsonString: string = JSON.stringify(objectToCopy);
    return JSON.parse(jsonString);
  }

  /** check for available cell-data -> else no formatting
   * @param rowData
   * @param fieldName
   * @param format
   */
  dataIsDefined(rowData: any, fieldName: string, format: ColumnFormat): boolean {
    /** Action-icon has no data and check may have null value */
    if (format && (format === ColumnFormat.ACTIONICON || format === ColumnFormat.CHECK || format === ColumnFormat.BOOLEAN)) {
      return true;
    }
    try {
      return (rowData[fieldName] !== null && rowData[fieldName] !== undefined);
    } catch (e) {
      return false;
    }
  }

  /**
   * check for valid date - for table cell formatting
   * @param data
   */
  isValidDate(data: any): boolean {
    try {
      let tmpDate: Date = new Date(data);
      if (tmpDate.getDate()) {
        return true;
      }
    } catch (e) {
      return false;
    }
  }

  /**
   * filter illegal chars on input
   * @param event
   * @param illegalChars
   */
  checkIllegalChar(event, illegalChars: string = null) {
    if (illegalChars !== null && illegalChars !== undefined) {
      if (illegalChars.indexOf(event.key) !== -1) {
        event.preventDefault();
      }
    }
  }

  /**
   * allow only specific chars defined over RegExp
   * @param event
   * @param allowed
   */
  allowOnlyChars(event: Event, allowed: RegExp) {
    const input = event.target as HTMLInputElement;
    const cleaned = input.value.replace(allowed, '');
    if (cleaned !== input.value) {
      input.value = cleaned;
    }
  }

  attachmentMimeTypes(): string {
    let mimeTypes: string = 'application/pdf,';
    mimeTypes += 'application/msword,';
    mimeTypes += 'application/msexcel,';
    mimeTypes += 'application/mspowerpoint,';
    mimeTypes += 'application/vnd.ms-powerpoint,';
    mimeTypes += 'application/vnd.openxmlformats-officedocument.wordprocessingml.document,';
    mimeTypes += 'application/vnd.openxmlformats-officedocument.wordprocessingml.template,';
    mimeTypes += 'application/vnd.ms-word.document.macroEnabled.12,';
    mimeTypes += 'application/vnd.ms-word.template.macroEnabled.12,';
    mimeTypes += 'application/vnd.openxmlformats-officedocument. spreadsheetml.sheet,';
    mimeTypes += 'application/vnd.openxmlformats-officedocument.presentationml.presentation,';
    mimeTypes += 'application/vnd.openxmlformats-officedocument.presentationml.slideshow,';
    mimeTypes += 'application/vnd.ms-powerpoint.presentation.macroEnabled.12,';
    mimeTypes += 'application/xml,';
    mimeTypes += 'application/zip,application/octet-stream,application/x-zip-compressed,multipart/x-zip,';
    mimeTypes += '.xls,';
    mimeTypes += '.zip,';
    mimeTypes += 'image/gif,';
    mimeTypes += 'image/png,';
    mimeTypes += 'image/jpeg,';
    mimeTypes += 'image/bmp,';
    mimeTypes += 'image/svg+xml';
    return mimeTypes;
  }

  toUppercase(value: string): string {
    if (value) {
      return value.toUpperCase();
    }
    return '';
  }

  /**
   * build Hash from data-array
   * @param data
   */
  dataArrayHash(data: any[]): string {
    return objectHash(data);
  }

  /**
   * Simple check for valid Mail-Address
   * @param email
   */
  isValidEmail(email: string): boolean {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return regex.test(email.trim());
  }

  /**
   * Simple check for valid Mail-Address-List (comma-separated)
   * @param emailList
   */
  isValidEmailList(emailList: string): boolean {
    const emails = emailList.split(',').map(e => e.trim());
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
    return emails.every(email => regex.test(email));
  }

  /**
   * Simple check for a valid URL.
   * If a value is entered, it must be a well-formed URL starting with https://
   * @param url
   */
  isValidHttpsUrl(url: string): boolean {
    const value = (url ?? '').trim();

    if (value.length === 0) {
      return false;
    }
    // Must literally start with https:// — the URL parser would otherwise
    // silently repair inputs like "https:example.de" or "https:/example.de"
    if (!value.toLowerCase().startsWith('https://')) {
      return false;
    }

    try {
      const parsed = new URL(value);
      return parsed.protocol === 'https:' && parsed.hostname.includes('.');
    } catch {
      return false;
    }
  }
  /**
   * default LazyLoad-Event for fetching eager data
   */
  eagerLazyLoadEvent(sortField: string = 'id', sortOrder: number = -1): LazyLoadEvent {
    return {
      first: 0,
      rows: 99999,
      sortField: sortField,
      sortOrder: sortOrder,
      filters: {}
    };

  }
}
